import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '@/providers/ApiContext';
import {
  Box,
  Typography,
  Toolbar,
  Divider,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import { readDatabase, writeDatabase } from '@/actions/adminDb';
import type { AdminDbReadResponse, AdminDbWriteResponse } from '@/types';

// SQL validation functions
const validateSqlCommand = (sql: string, enableWrites: boolean): string[] => {
  const errors: string[] = [];

  // Trim and normalize the SQL
  const normalizedSql = sql.trim();

  if (!normalizedSql) {
    errors.push('SQL command cannot be empty');
    return errors;
  }

  // Check for multiple statements (semicolons)
  const semicolonCount = (normalizedSql.match(/;/g) || []).length;
  if (semicolonCount > 1) {
    errors.push(
      'Only single SQL statements are allowed (multiple semicolons detected)',
    );
  }

  // Check for multiple statements (GO statements for SQL Server)
  if (normalizedSql.toLowerCase().includes('go')) {
    errors.push('GO statements are not allowed (use single statements only)');
  }

  // Extract the first word (SQL verb)
  const firstWord = normalizedSql.split(/\s+/)[0].toLowerCase();

  // Define read-only and write verbs
  const readVerbs = ['select', 'show', 'describe', 'desc', 'explain', 'help'];
  const writeVerbs = [
    'insert',
    'update',
    'delete',
    'create',
    'drop',
    'alter',
    'truncate',
    'replace',
  ];

  if (enableWrites) {
    // Write mode: only allow write verbs
    if (!writeVerbs.includes(firstWord)) {
      errors.push(
        `Write mode enabled but command starts with '${firstWord}'. Write mode only allows: ${writeVerbs.join(', ')}`,
      );
    }
  } else {
    // Read mode: only allow read verbs
    if (!readVerbs.includes(firstWord)) {
      errors.push(
        `Read mode but command starts with '${firstWord}'. Read mode only allows: ${readVerbs.join(', ')}`,
      );
    }
  }

  // Additional security checks
  const dangerousPatterns = [
    /drop\s+database/i,
    /drop\s+table\s+.*\s+cascade/i,
    /truncate\s+table/i,
    /delete\s+from\s+.*\s+where\s+1\s*=\s*1/i,
    /update\s+.*\s+set\s+.*\s+where\s+1\s*=\s*1/i,
  ];

  dangerousPatterns.forEach((pattern) => {
    if (pattern.test(normalizedSql)) {
      errors.push('Potentially dangerous SQL pattern detected');
    }
  });

  return errors;
};

interface DatabaseRow {
  id: number;
  [key: string]: unknown;
}

function AccQsureAdminDatabase(): React.JSX.Element {
  const api = useApi();

  const [sqlCommand, setSqlCommand] = useState<string>('');
  const [enableWrites, setEnableWrites] = useState<boolean>(false);
  const [results, setResults] = useState<AdminDbReadResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Validate SQL command whenever it changes or write toggle changes
  useEffect(() => {
    if (sqlCommand.trim()) {
      const errors = validateSqlCommand(sqlCommand, enableWrites);
      setValidationErrors(errors);
    } else {
      setValidationErrors([]);
    }
  }, [sqlCommand, enableWrites]);

  // Generate columns for DataGrid based on results
  const columns: GridColDef<DatabaseRow>[] = useMemo(() => {
    if (!results?.results?.length) return [];

    const firstRow = results.results[0];
    const cols: GridColDef<DatabaseRow>[] = Object.keys(firstRow).map((key) => ({
      field: key,
      headerName: key,
      flex: 1,
      minWidth: 120,
      // Remove the problematic valueGetter and let DataGrid handle the values directly
    }));

    console.log('Generated columns:', cols);
    return cols;
  }, [results]);

  // Generate rows for DataGrid
  const rows: DatabaseRow[] = useMemo(() => {
    if (!results?.results?.length) return [];

    const processedRows: DatabaseRow[] = results.results.map((row, index) => {
      // Create a clean row object with proper field mapping
      const processedRow: DatabaseRow = { id: index };

      // Copy all properties from the original row, ensuring they're properly mapped
      Object.keys(row).forEach((key) => {
        // Convert null/undefined to empty string for better display
        processedRow[key] =
          row[key] === null || row[key] === undefined ? '' : row[key];
      });

      return processedRow;
    });

    console.log('Original results:', results.results);
    console.log('Processed rows:', processedRows);
    return processedRows;
  }, [results]);

  const executeSql = useCallback(async (): Promise<void> => {
    if (!sqlCommand.trim() || !api) return;

    // Check for validation errors before executing
    if (validationErrors.length > 0) {
      setError(`Validation failed: ${validationErrors.join('; ')}`);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setResults(null);

    try {
      if (enableWrites) {
        const data = await writeDatabase(api, sqlCommand) as AdminDbWriteResponse;
        let successMessage = `Write command executed successfully. Affected rows: ${data.affectedRows}`;
        if (data.insertId) {
          successMessage = `${successMessage}. Insert ID: ${data.insertId}`;
        }
        setSuccess(successMessage);
      } else {
        const data = await readDatabase(api, sqlCommand);
        console.log('Read database response:', data); // Debug logging
        setResults(data);
      }

      // Clear the write toggle after execution
      setEnableWrites(false);
    } catch (err) {
      console.error('Error executing SQL:', err);
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Failed to execute SQL command';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [sqlCommand, enableWrites, api, validationErrors]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      executeSql();
    }
  };

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <Typography variant='h6' sx={{ mr: 4 }}>
          Database Commands
        </Typography>
      </Toolbar>
      <Divider />

      <Box sx={{ p: 3 }}>
        {/* Results Display Area */}
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 3,
            minHeight: '300px',
            maxHeight: '500px',
            overflow: 'auto',
          }}
        >
          <Typography variant='h6' gutterBottom>
            Query Results
          </Typography>

          {loading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity='success' sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {results && !loading && (
            <Box>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                Row count: {results.row_count}{' '}
                {results.truncated && '(truncated)'}
              </Typography>

              {/* Debug information */}
              <Box
                sx={{
                  mb: 2,
                  p: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                }}
              >
                <Typography variant='caption' color='text.secondary'>
                  Debug: {columns.length} columns, {rows.length} rows
                </Typography>
                {rows.length > 0 && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mt: 1 }}
                  >
                    First row keys: {Object.keys(rows[0]).join(', ')}
                  </Typography>
                )}
              </Box>

              {results.results &&
              results.results.length > 0 &&
              columns.length > 0 ? (
                <DataGrid
                  rows={rows}
                  columns={columns}
                  paginationModel={{ page: 0, pageSize: 10 }}
                  pageSizeOptions={[10, 25, 50]}
                  autoHeight
                  disableRowSelectionOnClick
                  getRowId={(row) => row.id}
                  sx={{
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: '#f5f5f5',
                      fontWeight: 'bold',
                    },
                  }}
                />
              ) : results.results && results.results.length > 0 ? (
                // Fallback: Simple table display
                <Box sx={{ overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        {Object.keys(results.results[0]).map((key) => (
                          <th
                            key={key}
                            style={{
                              padding: '8px',
                              border: '1px solid #ddd',
                              textAlign: 'left',
                            }}
                          >
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.results.map((row, index) => (
                        <tr key={index}>
                          {Object.keys(row).map((key) => (
                            <td
                              key={key}
                              style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                              }}
                            >
                              {row[key] === null || row[key] === undefined
                                ? 'NULL'
                                : String(row[key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  No results returned
                </Typography>
              )}
            </Box>
          )}

          {!results && !loading && !error && !success && (
            <Typography variant='body2' color='text.secondary'>
              Execute a SQL command to see results here
            </Typography>
          )}
        </Paper>

        {/* SQL Input and Controls */}
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant='h6' gutterBottom>
            SQL Command
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            variant='outlined'
            placeholder='Enter your SQL command here...'
            value={sqlCommand}
            onChange={(e) => setSqlCommand(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{ mb: 2 }}
            error={validationErrors.length > 0}
            helperText={
              validationErrors.length > 0 ? validationErrors.join('; ') : ''
            }
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={enableWrites}
                  onChange={(e) => setEnableWrites(e.target.checked)}
                  color='warning'
                />
              }
              label='Enable Writes (UPDATE, DELETE, INSERT)'
            />

            {enableWrites && (
              <Alert severity='warning' sx={{ flex: 1 }}>
                Warning: Write commands will modify the database. Use with
                caution!
              </Alert>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant='contained'
              onClick={executeSql}
              disabled={
                !sqlCommand.trim() || loading || validationErrors.length > 0
              }
              color={enableWrites ? 'warning' : 'primary'}
            >
              {loading ? <CircularProgress size={20} /> : 'Execute SQL'}
            </Button>

            <Button
              variant='outlined'
              onClick={() => {
                setSqlCommand('');
                setResults(null);
                setError(null);
                setSuccess(null);
                setEnableWrites(false);
                setValidationErrors([]);
              }}
              disabled={loading}
            >
              Clear
            </Button>
          </Box>

          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ mt: 1, display: 'block' }}
          >
            Tip: Press Ctrl+Enter (or Cmd+Enter on Mac) to execute the command
          </Typography>
        </Paper>
      </Box>
    </>
  );
}

export default AccQsureAdminDatabase;
